import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { CheckCircle2, Download, FileText, Mail } from "lucide-react";
import { toast } from "sonner";
import { subscribeNewsletter } from "@/lib/api/intake.functions";

/**
 * Lead-magnet block: visitors trade their email for a free
 * "Labour Rights & CCMA Quick-Start Checklist" (PDF, delivered by email).
 * Uses the existing newsletter_subscribers table via subscribeNewsletter.
 */
const CONSENT_TEXT =
  "I agree to receive the free Labour Rights & CCMA checklist plus occasional labour-law tips from Nkadimeng Danny Legal & Compliance Support. I can unsubscribe at any time.";

export function LeadMagnetSection() {
  const subscribe = useServerFn(subscribeNewsletter);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  return (
    <section
      id="free-checklist"
      className="border-y border-border bg-secondary/40 py-16"
    >
      <div className="container-narrow grid gap-10 md:grid-cols-[1.1fr_1fr] md:items-center">
        <div>
          <span className="gold-rule" />
          <p className="text-xs uppercase tracking-[0.25em] text-gold-ink">
            Free download
          </p>
          <h2 className="mt-2 font-display text-3xl text-navy md:text-4xl">
            Know your rights before you sign, speak or settle.
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Get our <strong className="text-foreground">Labour Rights &amp;
            CCMA Quick-Start Checklist</strong> — a plain-language PDF that
            walks you through the first 7 steps to take after a dismissal,
            disciplinary charge or unfair labour practice. Written for South
            African workers and small employers.
          </p>

          <ul className="mt-5 space-y-2 text-sm text-foreground/85">
            {[
              "The 30-day CCMA referral deadline explained",
              "LRA Form 7.11 — what to put in each section",
              "Documents to gather before your hearing",
              "A short script for talking to your employer",
            ].map((b) => (
              <li key={b} className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-lifted md:p-8">
          {done ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <CheckCircle2 className="h-10 w-10 text-gold" />
              <h3 className="font-display text-2xl text-navy">Check your inbox</h3>
              <p className="text-sm text-muted-foreground">
                We'll email your checklist shortly. If you don't see it within
                a few minutes, check your spam folder.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-4 flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-gold-ink">
                <FileText className="h-4 w-4" /> Instant PDF · 100% free
              </div>
              <h3 className="font-display text-2xl text-navy">
                Send my checklist
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Enter your email and we'll send the PDF plus occasional
                practical legal tips. Unsubscribe any time. POPIA-compliant.
              </p>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!email) return;
                  if (!consent) {
                    toast.error(
                      "Please tick the POPIA consent box so we can email you the checklist.",
                    );
                    return;
                  }
                  setLoading(true);
                  try {
                    await subscribe({
                      data: {
                        email,
                        consent: true,
                        consentText: CONSENT_TEXT,
                      },
                    });
                    setDone(true);
                    toast.success("You're on the list — checklist on its way.");
                    setEmail("");
                    setConsent(false);
                  } catch (err) {
                    toast.error(
                      err instanceof Error ? err.message : "Could not subscribe",
                    );
                  } finally {
                    setLoading(false);
                  }
                }}
                className="mt-5 space-y-3"
              >
                <label className="block text-sm font-medium text-foreground">
                  Email address
                  <div className="relative mt-1.5">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full rounded-md border border-input bg-background py-2.5 pl-9 pr-3 text-sm focus:border-gold focus:outline-none"
                    />
                  </div>
                </label>
                <label className="flex items-start gap-2 text-xs leading-snug text-foreground/80">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-gold"
                    required
                  />
                  <span>
                    <strong className="text-foreground">POPIA consent —</strong>{" "}
                    {CONSENT_TEXT} See our{" "}
                    <a href="/privacy" className="underline hover:text-gold">
                      privacy policy
                    </a>{" "}
                    or{" "}
                    <a href="/unsubscribe" className="underline hover:text-gold">
                      unsubscribe here
                    </a>
                    .
                  </span>
                </label>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-gold px-5 py-3 text-sm font-semibold text-gold-foreground shadow-gold hover:brightness-95 disabled:opacity-60"
                >
                  <Download className="h-4 w-4" />
                  {loading ? "Sending…" : "Send me the free checklist"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
